/**
 * Allowed file extensions for staff document uploads.
 */
export const ALLOWED_EXTENSIONS = ["jpg", "jpeg", "png", "doc", "docx", "pdf"];

/**
 * Returns the lowercase extension of a filename string.
 * e.g. "aadhaar.PDF" → "pdf", "offer_letter.docx" → "docx"
 * Returns "" if no extension found.
 */
export const getExtension = (filename) => {
  if (!filename || typeof filename !== "string") return "";
  const parts = filename.split(".");
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : "";
};

/**
 * Validates a single document entry.
 * Entry shape: { document_type: string, files: [{ file_name: string }] }
 * Returns an error object — only keys with errors are present.
 */
export const validateDocumentEntry = (entry) => {
  const errors = {};
  const hasType = entry?.document_type && entry.document_type.trim() !== "";
  const hasFiles = Array.isArray(entry?.files) && entry.files.some((f) => f?.file_name?.trim());
  if (hasFiles && !hasType) errors.document_type = "Document type is required";
  if (hasType && !hasFiles) errors.files = "At least one file is required";
  return errors;
};

/**
 * Maps frontend document_type values → backend identify_document keys.
 * Values that don't have a dedicated key fall into other_documents.
 */
const DOC_TYPE_TO_BE_KEY = {
  pan_card: "pan_card",
  passport: "passport",
  voter_id: "voter_id",
  driving_licence: "driving_license",
  utility_bill: "utility_bill",
  rent_agreement: "rent_agreement",
  marksheet_10_12: "mark_sheet_10th_12th",
  degree_certificate: "degree_certificate",
  diploma_certificate: "diploma_certificate",
  offer_letter: "offer_letter",
  appointment_letter: "appointment_letter",
  experience_letter: "previous_experience_letter",
  relieving_letter: "relieving_letter",
  pf_form_11: "pf_form_11",
  uan_card: "uan_card",
  esic_card: "esic_card",
  form_16: "tds_form_16",
  cancelled_cheque: "cancelled_cheque",
  bank_passbook: "bank_passbook",
  other_document: "other_documents",
  aadhaar_address: "other_documents",
};

export const buildDocumentPayload = (documents) => {
  const arr = Array.isArray(documents) ? documents : [];

  // Aadhaar — mandatory, goes into staffProfile.aadhaar_card
  const aadhaarEntry = arr.find((d) => d.document_type === "aadhaar_card");
  const aadhaarFile = aadhaarEntry?.files?.find((f) => f?.file_name)?.file_name || null;

  // All other entries → identify_document object
  const identifyDocument = {};
  arr
    .filter((d) => d.document_type !== "aadhaar_card")
    .forEach((entry) => {
      const beKey = DOC_TYPE_TO_BE_KEY[entry.document_type] || "other_documents";
      const fileNames = (entry.files || []).map((f) => f?.file_name).filter(Boolean);
      if (fileNames.length > 0) {
        if (identifyDocument[beKey]) {
          identifyDocument[beKey] = [...identifyDocument[beKey], ...fileNames];
        } else {
          identifyDocument[beKey] = fileNames;
        }
      }
    });

  return { aadhaarFile, identifyDocument };
};

/**
 * Converts a backend identify_document object back to the frontend documents array format.
 * Used when loading existing staff data in edit mode.
 */
export const parseIdentifyDocument = (identifyDoc) => {
  if (!identifyDoc || typeof identifyDoc !== "object") return [];

  // Reverse map: backend key → frontend document_type
  const BE_KEY_TO_DOC_TYPE = Object.fromEntries(
    Object.entries(DOC_TYPE_TO_BE_KEY).map(([fe, be]) => [be, fe]),
  );
  // Handle many-to-one (aadhaar_address and other_document both map to other_documents)
  BE_KEY_TO_DOC_TYPE["other_documents"] = "other_document";

  return Object.entries(identifyDoc)
    .filter(([, files]) => Array.isArray(files) && files.length > 0)
    .map(([beKey, files]) => ({
      document_type: BE_KEY_TO_DOC_TYPE[beKey] || beKey,
      files: files.map((f) => ({ file_name: f })),
      mandatory: false,
    }));
};

/**
 * Migrates a legacy single other_document string to the new multi-file format.
 * Returns [{ document_type: "other_document", files: [{ file_name: legacyValue }] }]
 */
export const migrateLegacyDocument = (legacyValue) => [
  { document_type: "other_document", files: [{ file_name: legacyValue }] },
];

/**
 * Migrates a legacy flat array [{ document_type, file_name }] to the new grouped format.
 * Groups entries with the same document_type together.
 * Also merges legacy tenth_marksheet / twelfth_marksheet → marksheet_10_12.
 */
export const migrateFlatDocuments = (flatDocs) => {
  if (!Array.isArray(flatDocs)) return [];
  const map = {};
  flatDocs.forEach(({ document_type, file_name }) => {
    // Merge legacy separate marksheet types into the combined key
    const key =
      document_type === "tenth_marksheet" || document_type === "twelfth_marksheet"
        ? "marksheet_10_12"
        : document_type;
    if (!map[key]) map[key] = { document_type: key, files: [] };
    if (file_name) map[key].files.push({ file_name });
  });
  return Object.values(map);
};

/**
 * Returns a new array with the element at the given index removed.
 * Preserves order of all other elements.
 */
export const removeAt = (arr, index) => {
  if (!Array.isArray(arr)) return [];
  if (index < 0 || index >= arr.length) return [...arr];
  return [...arr.slice(0, index), ...arr.slice(index + 1)];
};

/**
 * Validates a file object for upload.
 * Returns { valid: true } or { valid: false, error: string }
 */
export const validateFileForUpload = (file) => {
  if (!file) return { valid: false, error: "No file selected" };
  const ext = getExtension(file.name);
  if (!ALLOWED_EXTENSIONS.includes(ext)) {
    return { valid: false, error: "Only JPG, PNG, PDF, DOC, and DOCX files are allowed" };
  }
  if (file.size > 1_048_576) {
    return { valid: false, error: "File size must not exceed 1 MB" };
  }
  return { valid: true };
};
